import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Eye } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button, Select } from './ui';
import { dayLabel, localDate, pick } from './programAdminModel';
import { DayTab, DayTabs } from './programAdminStyles';

// "Name — 05 Nov 2026": the event dropdown shows what the admin picks an event by. The options come
// from GET /api/cms/events/options ({id, nameDe, nameEn, startsOn, endsOn, programStatus, past}).
const optionLabel = (option, language, intl) => {
  const name = pick(language, option.nameDe, option.nameEn) || `#${option.id}`;
  if (!option.startsOn) return name;
  const from = intl.formatDate(localDate(option.startsOn), { day: '2-digit', month: 'short', year: 'numeric' });
  const to = option.endsOn ? intl.formatDate(localDate(option.endsOn), { day: '2-digit', month: 'short', year: 'numeric' }) : null;
  return to && to !== from ? `${name} — ${from} – ${to}` : `${name} — ${from}`;
};

// The top bar of the event screen (plan §7.7 item 1): the event is chosen here, the program
// management of that event is everything below it. `events` is the answer of
// GET /api/cms/events/options: the upcoming events first, the past ones greyed in their own group.
// The preview button opens the public program as the section renders it.
const EventBar = ({ events = [], eventId, program, onSelectEvent, onPreviewPublic }) => {
  const intl = useIntl();
  const { language } = useLanguage();
  const settings = program?.event;
  const upcoming = events.filter((event) => !event.past);
  const past = events.filter((event) => event.past);
  const options = (list) => list.map((event) => (
    <option key={event.id} value={event.id} style={event.past ? { color: '#94a3b8' } : undefined}>
      {optionLabel(event, language, intl)}
    </option>
  ));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '.75rem', marginBottom: '1.1rem' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', fontSize: '.82rem', fontWeight: 600, color: '#334155' }}>
        <FormattedMessage id="programAdmin.event" defaultMessage="Event" />
        <Select value={eventId ?? ''} onChange={(e) => onSelectEvent?.(Number(e.target.value))} style={{ minWidth: '22rem' }}>
          {upcoming.length > 0 && (
            <optgroup label={intl.formatMessage({ id: 'programAdmin.event.upcoming', defaultMessage: 'Upcoming' })}>
              {options(upcoming)}
            </optgroup>
          )}
          {past.length > 0 && (
            <optgroup label={intl.formatMessage({ id: 'programAdmin.event.past', defaultMessage: 'Past' })}>
              {options(past)}
            </optgroup>
          )}
        </Select>
      </label>

      {settings && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.5rem', fontSize: '.8rem', color: '#475569' }}>
          <span style={{
            fontSize: '.68rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', padding: '.15rem .5rem',
            background: settings.programStatus === 'PUBLISHED' ? '#dcfce7' : '#fef3c7',
            color: settings.programStatus === 'PUBLISHED' ? '#166534' : '#92400e',
          }}>
            {settings.programStatus === 'PUBLISHED'
              ? <FormattedMessage id="programAdmin.status.published" defaultMessage="Published" />
              : <FormattedMessage id="programAdmin.status.draft" defaultMessage="Draft" />}
          </span>
          <span>
            {intl.formatMessage({ id: 'programAdmin.dates', defaultMessage: '{from} to {to}' }, {
              from: intl.formatDate(new Date(`${settings.startsOn}T00:00:00`), { day: '2-digit', month: '2-digit', year: 'numeric' }),
              to: intl.formatDate(new Date(`${settings.endsOn}T00:00:00`), { day: '2-digit', month: '2-digit', year: 'numeric' }),
            })}
          </span>
        </div>
      )}

      <Button type="button" style={{ marginLeft: 'auto' }} onClick={onPreviewPublic}>
        <Eye size={15} aria-hidden="true" />
        <FormattedMessage id="programAdmin.previewPublic" defaultMessage="Preview public program" />
      </Button>
    </div>
  );
};

// The areas of the event screen. The three that are not built yet are still tabs, so the screen the
// owner reviews is the whole screen (plan §7.7 item 3).
export const AreaTabs = ({ areas, active, onSelect }) => (
  <div role="tablist" aria-label="Areas" style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1rem' }}>
    {areas.map((area) => (
      <button
        key={area.key}
        type="button"
        role="tab"
        aria-selected={active === area.key}
        onClick={() => onSelect?.(area.key)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '.4rem', border: 0, background: 'transparent',
          font: 'inherit', fontWeight: 700, padding: '.6rem .9rem', marginBottom: -2, cursor: 'pointer',
          color: active === area.key ? '#111D4E' : '#64748b',
          borderBottom: `2px solid ${active === area.key ? '#47ABDB' : 'transparent'}`,
        }}
      >
        {area.label}
      </button>
    ))}
  </div>
);

// The days of the program, as tabs (plan §7.7 item 2: the day tabs of the public section).
export const DayTabsBar = ({ days, activeId, language, onSelect }) => {
  const intl = useIntl();
  return (
    <DayTabs>
      {days.map((day) => (
        <DayTab key={day.id ?? 'none'} type="button" $active={activeId === day.id} onClick={() => onSelect?.(day.id)}>
          {dayLabel(intl, language, day)}
          {day.day && (
            <span style={{ color: '#64748b', fontWeight: 600 }}>
              {intl.formatDate(localDate(day.day), { day: '2-digit', month: '2-digit' })}
            </span>
          )}
        </DayTab>
      ))}
    </DayTabs>
  );
};

export default EventBar;
